import React from "react"
import PropTypes from "prop-types"
import { Button, Col, Flex, Row, Select } from "antd"
import Input from "components/form/Input"
import InputError from "components/form/InputError"
import Icon from "components/common/Icon"
import IconWithBg from "ui/IconWithBg"
import { Delete } from "ui/Icon/DSIcon/Delete"
import { PlusIcon } from "ui/Icon/DSIcon/Plus"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import styles from "./Form.module.css"

export default function ExpenseLineItems({ expenseItems, onItemChange, onAddItem, onRemoveItem, categories, onOpenAddCategory }) {
  const { t } = useTranslation()
  const filteredItems = expenseItems.filter((item) => !item._destroy)

  return (
    <div>
      <Row align="start" gutter={[20, 10]} className="mb-xs">
        <Col span={filteredItems.length > 1 ? 6 : 7}>
          <Text fw="500" className="block">
            {t("expense.item_name")}*
          </Text>
        </Col>
        <Col span={filteredItems.length > 1 ? 6 : 7}>
          <Text fw="500" className="block">
            {t("expense.amount")}*
          </Text>
        </Col>
        <Col span={10}>
          <Text fw="500" className="block">
            {t("expense.category")}*
          </Text>
        </Col>
      </Row>

      {expenseItems.map(
        (item, idx) =>
          !item._destroy && (
            <Row align="center" gutter={[20, 10]} key={item.id ? `item-${item.id}` : `item-${idx}`} className="mb-m">
              <Col span={filteredItems.length > 1 ? 6 : 7}>
                <Input
                  type="text"
                  name="name"
                  value={item.name}
                  onChange={(e) => onItemChange(idx, "name", e.target.value)}
                  withError
                />
              </Col>
              <Col span={filteredItems.length > 1 ? 6 : 7}>
                <div className={styles.amountInputWrapper}>
                  <Text className={styles.amountDollar}>$</Text>
                  <Input
                    type="number"
                    name="amount"
                    value={item.amount}
                    onChange={(e) => onItemChange(idx, "amount", e.target.value)}
                    min={0}
                    step="any"
                    withError
                    className={`fs-6 ${styles.amountInput}`}
                  />
                </div>
              </Col>
              <Col span={10}>
                <Select
                  className={`w-full ${styles.categorySelect}`}
                  value={item?.expense_category_id === "add-new-type" ? undefined : item?.expense_category_id || undefined}
                  onChange={(value) => {
                    if (value === "add-new-type") {
                      onOpenAddCategory()
                      return
                    }
                    onItemChange(idx, "expense_category_id", value)
                  }}
                  style={{ minWidth: 140 }}
                  suffixIcon={<Icon iconName="SidebarToggle" size={16} className="text-dark text-opacity-50" />}
                  placeholder="Choose Category"
                  options={[
                    ...categories.map((category) => ({
                      value: category.id,
                      label: category.name
                    })),
                    {
                      value: "add-new-type",
                      label: (
                        <Text
                          fw="500"
                          onClick={(e) => {
                            e.stopPropagation()
                            onOpenAddCategory()
                          }}
                        >
                          {t("expense.add_new_type")}
                        </Text>
                      )
                    }
                  ]}
                />
                <InputError field="category" className="mt-1 mb-n3" />
              </Col>
              {filteredItems.length > 1 && (
                <Col span={2}>
                  <Flex align="end" className="h-full pb-xs" onClick={() => onRemoveItem(idx)}>
                    <IconWithBg className={styles.deleteIcon} height={15} width={15} icon={<Delete fill="#ff0000" />} />
                  </Flex>
                </Col>
              )}
            </Row>
          )
      )}

      <Flex justify="start" className="w-full">
        <Button className="p-0" type="link" color="primary" variant="link" onClick={onAddItem}>
          <PlusIcon className="me-1" />
          {t("expense.add_line_item")}
        </Button>
      </Flex>
    </div>
  )
}

ExpenseLineItems.propTypes = {
  expenseItems: PropTypes.array.isRequired,
  onItemChange: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
  onOpenAddCategory: PropTypes.func.isRequired
}
